import React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/system';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';

export type TypeOption = {
  id: number;
  name: string;
};

type ButtonMenuProps = {
  text: string;
  options: TypeOption[];
  onClick: (id: number) => void;
  srcStartIcon?: string;
  srcEndIcon?: string;
  sx?: SxProps<Theme>;
  sxIcon?: SxProps<Theme>;
  sxButton?: SxProps<Theme>;
  sxText?: SxProps<Theme>;
  width?: string;
  disabled?: boolean;
};

const ButtonMenu: React.FC<ButtonMenuProps> = (props) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleClickOption = (id: number) => (event: Event | React.SyntheticEvent) => {
    handleClose(event);
    props.onClick(id);
  }

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Box>
      <BtnOutlineGradient
        ref={anchorRef}
        id='composition-button'
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        handleButtonClick={handleToggle}
        text={props.text} 
        disabled={props.disabled}
      />
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id='composition-menu'
                  aria-labelledby='composition-button'
                  onKeyDown={handleListKeyDown}
                  sx={{ 
                    width: '130px',
                    mb: '5px'
                  }}
                >
                  {props.options.map((option, index: number) => (
                    <MenuItem
                      key={index}
                      onClick={handleClickOption(option.id)}
                      sx={{ 
                        minHeight: '48px !important',
                        borderBottom: index === props.options.length - 1 ? '0px' : '1px solid #F3F3F3'
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

export default ButtonMenu;